import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Smartphone, Sidebar, MapPin, ExternalLink } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description?: string;
  imageUrl: string;
  targetUrl?: string;
  placement: string;
  pages: string[];
  onConfirm?: () => void;
  confirmLabel?: string;
}

export function BannerPreviewModal({ open, onOpenChange, title, description, imageUrl, targetUrl, placement, pages, onConfirm, confirmLabel = 'Submit for review' }: Props) {
  const showSidebar = ['right_sidebar', 'left_sidebar'].includes(placement) || pages.some((p) => ['buy', 'rent', 'agencies', 'commercial', 'projects', 'plots'].includes(p));
  const showInlineMobile = placement === 'agencies_inline_mobile' || pages.includes('agencies');
  const showCitySection = pages.includes('agencies') || pages.includes('buy') || pages.includes('rent');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Banner preview</DialogTitle>
          <DialogDescription>
            See how <strong>{title || 'your banner'}</strong> will render across each placement before it's submitted for review.
          </DialogDescription>
        </DialogHeader>

        {!imageUrl ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Upload an image to preview your banner.</p>
        ) : (
          <Tabs defaultValue="sidebar" className="w-full">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="sidebar"><Sidebar className="h-4 w-4 mr-1" /> Sidebar</TabsTrigger>
              <TabsTrigger value="mobile"><Smartphone className="h-4 w-4 mr-1" /> Mobile inline</TabsTrigger>
              <TabsTrigger value="city"><MapPin className="h-4 w-4 mr-1" /> City section</TabsTrigger>
            </TabsList>

            {/* Desktop sidebar */}
            <TabsContent value="sidebar" className="mt-4">
              <PreviewFrame label="Desktop · /agencies or /buy">
                <div className="flex gap-4">
                  <div className="flex-1 bg-muted/30 rounded p-4 space-y-2">
                    <div className="h-4 w-1/3 bg-muted rounded" />
                    <div className="h-3 w-2/3 bg-muted rounded" />
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="aspect-video bg-muted rounded" />
                      <div className="aspect-video bg-muted rounded" />
                      <div className="aspect-video bg-muted rounded" />
                      <div className="aspect-video bg-muted rounded" />
                    </div>
                  </div>
                  <div className="w-[300px] shrink-0">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Sponsored</p>
                    <a href={targetUrl || '#'} onClick={(e) => !targetUrl && e.preventDefault()}
                       className="block border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-card">
                      <img src={imageUrl} alt={title} className="w-full aspect-square object-cover" />
                      <div className="p-3">
                        <p className="text-sm font-medium line-clamp-1">{title || 'Your banner title'}</p>
                        {description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{description}</p>}
                        {targetUrl && <p className="text-[10px] text-primary mt-1 flex items-center gap-1"><ExternalLink className="h-2.5 w-2.5" />{new URL(targetUrl).hostname}</p>}
                      </div>
                    </a>
                  </div>
                </div>
              </PreviewFrame>
              {!showSidebar && <Note text="Heads up: your placement/page selection won't normally trigger sidebar rendering." />}
            </TabsContent>

            {/* Mobile inline */}
            <TabsContent value="mobile" className="mt-4">
              <PreviewFrame label="Mobile · inline below agency list">
                <div className="mx-auto w-[320px] border rounded-2xl bg-background overflow-hidden">
                  <div className="bg-muted/40 p-3 space-y-2">
                    <div className="h-4 w-2/3 bg-muted rounded" />
                    <div className="border rounded p-2 bg-card">
                      <div className="flex gap-2">
                        <div className="h-12 w-12 bg-muted rounded" />
                        <div className="flex-1 space-y-1.5"><div className="h-2 bg-muted rounded w-3/4" /><div className="h-2 bg-muted rounded w-1/2" /></div>
                      </div>
                    </div>
                    <p className="text-[9px] uppercase tracking-wide text-muted-foreground pt-2">Sponsored</p>
                    <a href={targetUrl || '#'} onClick={(e) => !targetUrl && e.preventDefault()} className="block border rounded overflow-hidden bg-card">
                      <img src={imageUrl} alt={title} className="w-full aspect-[16/9] object-cover" />
                      <div className="p-2">
                        <p className="text-xs font-medium line-clamp-1">{title || 'Your banner title'}</p>
                      </div>
                    </a>
                  </div>
                </div>
              </PreviewFrame>
              {!showInlineMobile && <Note text="Inline-mobile slot only renders when 'agencies' is in pages or placement is set to Agencies (Mobile)." />}
            </TabsContent>

            {/* City section */}
            <TabsContent value="city" className="mt-4">
              <PreviewFrame label="City section header · e.g. Addis Ababa agencies">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Agencies in Addis Ababa</p>
                      <p className="text-xs text-muted-foreground">Sponsored placement</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">City banner</Badge>
                  </div>
                  <a href={targetUrl || '#'} onClick={(e) => !targetUrl && e.preventDefault()}
                     className="block border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-card">
                    <div className="grid grid-cols-[1fr_2fr]">
                      <img src={imageUrl} alt={title} className="aspect-square object-cover" />
                      <div className="p-4 space-y-2">
                        <p className="font-semibold">{title || 'Your banner title'}</p>
                        {description && <p className="text-sm text-muted-foreground line-clamp-3">{description}</p>}
                        {targetUrl && <Button size="sm" variant="outline" className="mt-2"><ExternalLink className="h-3 w-3 mr-1" /> Visit</Button>}
                      </div>
                    </div>
                  </a>
                </div>
              </PreviewFrame>
              {!showCitySection && <Note text="City-section banners require pages to include buy, rent, or agencies." />}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {onConfirm && (
            <Button onClick={onConfirm} disabled={!imageUrl || !title.trim()}>{confirmLabel}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-lg p-4 bg-background">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-3">
        <Building2 className="h-3 w-3" /> {label}
      </div>
      {children}
    </div>
  );
}

function Note({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground mt-2 px-1">⚠ {text}</p>;
}
